/**
 * Extension shell context — provides the active `HostServices` to extension UI
 * surfaces (sidebar panels, status-bar items, settings panels) so they can build
 * `ExtensionUIProps` and bridge `callRuntime` / `onRuntimeEvent` to the Engine.
 *
 * App layers wrap their shell in `<ExtensionShellProvider services={...}>`. When
 * no provider is mounted (e.g. an app that doesn't support extensions yet), the
 * contribution hooks still work but UI surfaces render nothing.
 */
import {
    createContext,
    createElement,
    useContext,
    type ReactNode,
} from "react";
import { useExtensionCommandBridge } from "./extension-command-bridge";
import type { HostServices } from "./host-services";

const ExtensionShellContext = createContext<HostServices | null>(null);

export interface ExtensionShellProviderProps {
    services: HostServices;
    children: ReactNode;
}

export function ExtensionShellProvider({
    services,
    children,
}: ExtensionShellProviderProps): ReactNode {
    // Bridge extension command contributions into the central command registry
    // for as long as the shell is mounted.
    useExtensionCommandBridge();
    return createElement(
        ExtensionShellContext.Provider,
        { value: services },
        children,
    );
}

/** Returns the active HostServices, or null when no provider is mounted. */
export function useExtensionShellServices(): HostServices | null {
    return useContext(ExtensionShellContext);
}
